/* eslint-disable no-param-reassign */
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import pick from 'lodash/pick';
import validator from 'validator';
import { capitalizeFirst, capitalizeWords } from './textTransform';

export const langs = ['sv', 'en', 'de'];
const textFields = ['title', 'description'];

export const toNumber = (value) => (value ? parseInt(value, 10) : null);

const emptyToNull = (value) => value || null;

const trimString = (value) => (typeof value === 'string' ? value.trim() : value);

const trimOrNullAll = (obj) => {
  const result = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    result[key] = emptyToNull(trimString(value));
  });

  return result;
};

const normalizeIcalUrls = (value) => {
  if (Array.isArray(value)) {
    return value.map((url) => `${url}`.trim()).filter(Boolean);
  }

  if (!value) {
    return [];
  }

  return `${value}`
    .split(',')
    .map((url) => url.trim())
    .filter(Boolean);
};

const numberFields = [
  'area',
  'beds',
  'extra_beds',
  'price_high_day',
  'price_high_week',
  'price_low_day',
  'price_low_week',
  'price_per_month',
  'region_id',
];

const nullOrFalseFields = ['commission', 'cottage_village'];

export const normalizeData = (values) => {
  const data = {};
  Object.keys(values).forEach((key) => {
    if (nullOrFalseFields.includes(key)) {
      data[key] = trimString(values[key]);
    } else if (numberFields.includes(key)) {
      data[key] = toNumber(values[key]);
    } else {
      data[key] = emptyToNull(trimString(values[key]));
    }
  });

  return data;
};

export const getCompleted = (c) => ({
  base: !!(
    c.area &&
    c.beds &&
    (c.commission === false || (c.property_designation_type && c.rent_out_type))
  ),
  text: !!(c.title_sv && c.description_sv),
  position: !!(
    c.lat &&
    c.lng &&
    c.region_id &&
    (c.commission === false ||
      (c.address_street && c.address_postal_code && c.address_city && c.property_designation))
  ),
  location: !!(c.cities && c.cities.length),
  amenities: !!(c.amenities && c.amenities.length),
  pricing: !!(c.price_low_day && c.price_low_week && c.price_high_day && c.price_high_week),
  calendar:
    !!(c.calendar && c.calendar.length) &&
    (!c.commission || (c.calendar || []).some((day) => day.state === 5)),
  images: !!(c.images && c.images.length),
  payment: c.commission === true || c.commission === false,
  verify: !!c.verified_at || !!c.reviewed_at,
});

export const prepareUpdateCottageData = (values, dialog) => {
  const data = normalizeData(values);

  switch (dialog) {
    case 'base': {
      return {
        area: data.area,
        beds: data.beds,
        property_designation_type: data.property_designation_type,
        rent_out_type: data.rent_out_type,
        extra_beds: data.extra_beds,
        commission: data.commission,
      };
    }
    case 'text': {
      const result = {};

      textFields.forEach((field) => {
        langs.forEach((lang) => {
          result[`${field}_${lang}`] = capitalizeFirst(data[`${field}_${lang}`]) || null;
        });
      });

      return result;
    }
    case 'position': {
      return {
        lat: data.lat,
        lng: data.lng,
        region_id: data.region_id,
        address_street: capitalizeWords(data.address_street),
        address_postal_code: data.address_postal_code,
        address_city: capitalizeWords(data.address_city),
        property_designation: data.property_designation,
        property_designation_type: data.property_designation_type,
        cottage_village: !!data.cottage_village,
        rent_out_type: data.rent_out_type,
        hide_location: !!data.hide_location,
      };
    }
    case 'location': {
      return {
        tags: (data.tags || []).map((tag) => {
          if (isObject(tag)) {
            return tag.value;
          }

          return tag;
        }),
        cities: data.cities.reduce((acc, city) => {
          let value;
          if (isString(city) && city && city.trim().length) {
            value = capitalizeWords(city.trim());
          } else if (city && city.value) {
            value = capitalizeWords(city.value.trim());
          }
          if (value && !acc.some((c) => c.toLowerCase() === value.toLowerCase())) {
            return acc.concat([value]);
          }

          return acc;
        }, []),
      };
    }
    case 'amenities': {
      const amenities = Object.keys(values.amenities)
        .filter((key) => values.amenities[key].checked)
        .map((key) => ({ key, ...trimOrNullAll(values.amenities[key]), checked: undefined }));

      return { amenities };
    }
    case 'pricing': {
      return {
        price_low_day: data.price_low_day,
        price_low_week: data.price_low_week,
        price_high_day: data.price_high_day,
        price_high_week: data.price_high_week,
        price_per_month: data.price_per_month,
        pricefield_sv: capitalizeFirst(emptyToNull(trimString(data.pricefield_sv))),
        pricefield_en: capitalizeFirst(emptyToNull(trimString(data.pricefield_en))),
        pricefield_de: capitalizeFirst(emptyToNull(trimString(data.pricefield_de))),
      };
    }
    case 'calendar': {
      const icalUrl = normalizeIcalUrls(data.ical_url);

      return {
        calendar: data.calendar,
        ical_url: icalUrl.length > 0 ? icalUrl : null,
        ical_sync: icalUrl.length > 0,
      };
    }
    case 'images': {
      const images = Object.values(values.images)
        .sort((a, b) => a.weight - b.weight)
        .map(({ weight, ...other }) => trimOrNullAll(other));

      return {
        images,
        youtube_video: emptyToNull(trimString(data.youtube_video)),
      };
    }
    case 'payment': {
      return {
        commission: values.commission, // can be false or null
      };
    }
    case 'onSendForReview': {
      return {
        asked_for_review_at: data.asked_for_review_at,
      };
    }
    default: {
      return data;
    }
  }
};

const dacTypeAndDesignation = (data, errors, props) => {
  if (!data.rent_out_type) {
    errors.rent_out_type = props.intl.formatMessage({
      id: 'Required',
      defaultMessage: 'Required',
    });
  }

  if (!data.property_designation_type) {
    errors.property_designation_type = props.intl.formatMessage({
      id: 'Required',
      defaultMessage: 'Required',
    });
  }

  return errors;
};

/**
 * Check property designation input
 * Fastighetsbeteckning: 2 char groups followed by 1 number group E.g. "AB CD 123:1"
 * Lägenhet: 4 digits only
 * Stugby: One string, numbers and chars max length 5
 * @param {*} property_designation
 * @returns
 */
const isValidPropertyDesignation = (cottage) => {
  const badInput = new Set(['0000']);
  const propertyDesignation = cottage.property_designation.trim();

  if (cottage.cottage_village) {
    return (
      /^[a-zA-ZåäöÅÄÖ]{1,2}$/.test(propertyDesignation) ||
      (/^(?=.*[1-9])[a-zA-ZåäöÅÄÖ0-9]{1,5}$/.test(propertyDesignation) &&
        /[0-9]/.test(propertyDesignation) &&
        (propertyDesignation.match(/[a-zA-ZåäöÅÄÖ]/g) || []).length <= 2)
    );
  }

  // Check for known bad inputs
  if (
    !propertyDesignation ||
    propertyDesignation.length <= 1 ||
    badInput.has(propertyDesignation)
  ) {
    return false;
  }
  // Apartment: 4 digits only
  if (cottage.property_designation_type === 'APARTMENT') {
    return /^\d{4}$/.test(propertyDesignation);
  }
  // Fastighetsbeteckning
  return /^([a-zA-ZåäöÅÄÖ]+(?:-[a-zA-ZåäöÅÄÖ]+)? [a-zA-ZåäöÅÄÖ]+(?:-[a-zA-ZåäöÅÄÖ]+)?(?: [a-zA-ZåäöÅÄÖ]+(?:-[a-zA-ZåäöÅÄÖ]+)?)?(?: [a-zA-ZåäöÅÄÖ]+(?:-[a-zA-ZåäöÅÄÖ]+)?)? [0-9]+:?[0-9]*)$/.test(
    propertyDesignation,
  );
};

const dacAddress = (data, errors, props) => {
  if (!data.address_street) {
    errors.address_street = props.intl.formatMessage({
      id: 'Required',
      defaultMessage: 'Required',
    });
  } else if (data.address_street?.length > 255) {
    errors.address_street = props.intl.formatMessage(
      {
        id: 'validation.max_chars',
        defaultMessage: 'Max number of chars: {max}',
      },
      { max: 255 },
    );
  }

  if (!data.address_postal_code) {
    errors.address_postal_code = props.intl.formatMessage({
      id: 'Required',
      defaultMessage: 'Required',
    });
  } else if (data.address_postal_code?.length > 50) {
    errors.address_postal_code = props.intl.formatMessage(
      {
        id: 'validation.max_chars',
        defaultMessage: 'Max number of chars: {max}',
      },
      { max: 50 },
    );
  }

  if (!data.address_city) {
    errors.address_city = props.intl.formatMessage({
      id: 'Required',
      defaultMessage: 'Required',
    });
  } else if (data.address_city?.length > 100) {
    errors.address_city = props.intl.formatMessage(
      {
        id: 'validation.max_chars',
        defaultMessage: 'Max number of chars: {max}',
      },
      { max: 100 },
    );
  }

  if (!data.property_designation) {
    errors.property_designation = props.intl.formatMessage({
      id: 'Required',
      defaultMessage: 'Required',
    });
  } else if (data.property_designation?.length > 255) {
    errors.property_designation = props.intl.formatMessage(
      {
        id: 'validation.max_chars',
        defaultMessage: 'Max number of chars: {max}',
      },
      { max: 255 },
    );
  } else if (!isValidPropertyDesignation(data)) {
    if (data.cottage_village) {
      errors.property_designation = props.intl.formatMessage({
        id: 'validation.property_designation.cottage_village',
        defaultMessage: 'Max 5 characters (Example: 12345)',
      });
    } else if (data.property_designation_type === 'APARTMENT') {
      errors.property_designation = props.intl.formatMessage({
        id: 'validation.property_designation.apartment',
        defaultMessage: 'Input 4 digits (Example: 1010)',
      });
    } else {
      errors.property_designation = props.intl.formatMessage({
        id: 'validation.property_designation',
        defaultMessage: 'Bad format (Example: Kommun Traktnamn 10:10)',
      });
    }
  }
};

const getErrors = (values, props, dialog) => {
  if (!Object.keys(values).length || !props.intl) {
    return {};
  }

  const errors = {};
  const data = prepareUpdateCottageData(values, dialog);

  switch (dialog) {
    // Base
    case 'base': {
      if (!data.area || data.area < 1) {
        errors.area = props.intl.formatMessage({
          id: 'cottages.validationArea',
          defaultMessage: 'Area must be a number and min 1',
        });
      }
      if (!data.beds || data.beds < 1) {
        errors.beds = props.intl.formatMessage({
          id: 'cottages.validationBeds',
          defaultMessage: 'Beds must be a number and min 1',
        });
      }
      if (data.extra_beds && data.extra_beds < 1) {
        errors.extra_beds = props.intl.formatMessage({
          id: 'cottages.validationExtraBeds',
          defaultMessage: 'Must be a number min 1',
        });
      }
      if (values.commission !== false) {
        dacTypeAndDesignation(data, errors, props);
      }
      break;
    }
    case 'images': {
      if (data.youtube_video) {
        const isValid = /^[a-zA-Z0-9_-]{6,50}$/.test(data.youtube_video);
        if (!isValid) {
          errors.youtube_video = props.intl.formatMessage({
            id: 'cottages.youtubeVideo.invalid',
            defaultMessage: 'Enter a valid YouTube video id',
          });
        }
      }
      break;
    }
    // Text
    case 'text': {
      if (!data.title_sv) {
        errors.title_sv = props.intl.formatMessage({
          id: 'cottages.validationTitleSv',
          defaultMessage: 'Swedish Title is required',
        });
      }
      if (!data.description_sv) {
        errors.description_sv = props.intl.formatMessage({
          id: 'cottages.validationDescriptionSv',
          defaultMessage: 'Swedish Description is required',
        });
      }
      break;
    }
    // Position
    case 'position': {
      if (!data.lat || !data.lng) {
        errors.position = props.intl.formatMessage({
          id: 'cottages.validationLatLng',
          defaultMessage: 'Lat / Lng is required',
        });
      }
      if (!data.region_id) {
        errors.region_id = props.intl.formatMessage({
          id: 'cottages.validationRegion',
          defaultMessage: 'Region is required',
        });
      }

      if (values.commission !== false) {
        dacAddress(data, errors, props);
      }
      break;
    }
    case 'location': {
      if (data.cities.length < 1) {
        errors.cities = props.intl.formatMessage({
          id: 'cottages.validationCities',
          defaultMessage: 'Add minimum 1 location',
        });
      }
      break;
    }
    case 'pricing': {
      if (data.price_low_day && data.price_high_day) {
        if (parseFloat(data.price_low_day) > parseFloat(data.price_high_day)) {
          errors.price_low_day = props.intl.formatMessage({
            id: 'cottages.priceLowRange',
            defaultMessage: 'Low price must be lower than high price',
          });
          errors.price_high_day = props.intl.formatMessage({
            id: 'cottages.priceHighRange',
            defaultMessage: 'High price must be higher than low price',
          });
        }
      }

      if (data.price_low_week && data.price_high_week) {
        if (parseFloat(data.price_low_week) > parseFloat(data.price_high_week)) {
          errors.price_low_week = props.intl.formatMessage({
            id: 'cottages.priceLowRange',
            defaultMessage: 'Low price must be lower than high price',
          });
          errors.price_high_week = props.intl.formatMessage({
            id: 'cottages.priceHighRange',
            defaultMessage: 'High price must be higher than low price',
          });
        }
      }

      break;
    }
    case 'calendar': {
      const icalUrl = normalizeIcalUrls(values.ical_url);
      const hasInvalidUrl = icalUrl.some(
        (url) =>
          !validator.isURL(url, {
            require_protocol: true,
            protocols: ['http', 'https', 'webcal'],
          }),
      );

      if (hasInvalidUrl) {
        errors.ical_url = props.intl.formatMessage({
          id: 'cottages.calendar.icalInvalid',
          defaultMessage: 'One or more iCal URLs are invalid',
        });
      }

      break;
    }
    default:
  }

  return errors;
};

export const validate = (values, props) => {
  let errors = {};
  const { dialog } = props;

  if (dialog) {
    return getErrors(values, props, dialog);
  }

  // Force validation for all required dialogs
  ['base', 'text', 'position', 'location', 'images', 'payment', 'pricing'].forEach(
    (requiredDialog) => {
      errors = { ...errors, ...getErrors(values, props, requiredDialog) };
    },
  );
  return errors;
};

export const validatePatched = (props) => (values) => {
  return validate(values, props);
};

export const validateDacCompliance = (props) => (data) => {
  const errors = {};
  data = normalizeData(data);
  dacTypeAndDesignation(data, errors, props);
  dacAddress(data, errors, props);

  return errors;
};

export const cleanDacComplianceData = (data) => {
  return pick(data, [
    'address_street',
    'address_postal_code',
    'address_city',
    'property_designation_type',
    'cottage_village',
    'rent_out_type',
    'property_designation',
  ]);
};
